/* eslint-disable prettier/prettier */
// src/hooks/useAuth.ts
/* prettier-ignore-file */
/* eslint-disable prettier/prettier */

import { useState, useCallback } from 'react';
import { authService } from '../services/authService';

interface LoginPayload {
	email: string;
	password: string;
}

interface RegisterPayload {
	email: string;
	password: string;
	fullname: string;

	username?: string;
	location?: string;
	phonenumber?: string;
	country?: string;
	timezone?: string;

	organizationname?: string;
	designation?: string;
	skills?: string[];
	areasofinterest?: string[];
	growthparameters?: string[];
	languages?: string[];

	yearsofexperience?: string;
	coachingexperience?: string[];
}

export const useAuth = () => {
	const [user, setUser] = useState<any>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// 🔹 Login handler
	const login = useCallback(async (payload: LoginPayload) => {
		try {
			setLoading(true);
			setError(null);

			const data = await authService.loginUser(payload);

			// Save token & user in localStorage

			localStorage.setItem('token', data.tokens.accessToken);
			localStorage.setItem('refreshtoken', data.tokens.refreshToken);

			localStorage.setItem('user', JSON.stringify(data.user));

			setUser(data.user);

			// 🔹 Notify the app that authentication has changed
			window.dispatchEvent(new Event('authChanged'));

			return data;
		} catch (err: any) {
			setError(err.message);
			throw err;
		} finally {
			setLoading(false);
		}
	}, []);

	// 🔹 Register handler
	const register = useCallback(async (data: RegisterPayload) => {
		setLoading(true);
		setError(null);
		try {
			const res = await authService.registerUser(data);
			console.log('REGISTER RESPONSE:', res);
			setUser(res.user);

			localStorage.setItem('token', res.tokens.accessToken);
			localStorage.setItem('refreshtoken', res.tokens.refreshToken);

			localStorage.setItem('user', JSON.stringify(res.user));

			// 🔹 Notify the app
			window.dispatchEvent(new Event('authChanged'));

			return res;
		} catch (err: any) {
			setError(err.message || 'Registration failed');
			throw err;
		} finally {
			setLoading(false);
		}
	}, []);

	const changePassword = useCallback(
		async (currentPassword: string, newPassword: string) => {
			try {
				setLoading(true);
				setError(null);

				const res = await authService.changePassword(
					currentPassword,
					newPassword
				);

				// After successful change, logout user automatically
				if (res?.success) {
					return res;
				}

				return res;
			} catch (err: any) {
				setError(err.message);
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	const forgotPassword = useCallback(async (email: string) => {
		try {
			setLoading(true);
			setError(null);

			const res = await authService.forgotPassword(email);
			return res;
		} catch (err: any) {
			setError(err.message);
			throw err;
		} finally {
			setLoading(false);
		}
	}, []);

	const handleAllSocialLogin = useCallback(async (provider: string) => {
		try {
			setLoading(true);
			setError(null);

			const res = await authService.socialLogin(provider);
			console.log('Social login response:', res);
			return res;
		} catch (err: any) {
			setError(err.message);
			throw err;
		} finally {
			setLoading(false);
		}
	}, []);

	const logout = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const refreshToken = localStorage.getItem('refreshtoken');

			if (!refreshToken) {
				throw new Error('No refresh token found');
			}

			await authService.logoutUser(refreshToken);

			// 🔥 Clear everything after backend success
			localStorage.removeItem('token');
			localStorage.removeItem('refreshtoken');
			localStorage.removeItem('user');
			localStorage.removeItem('cachedProfile');

			setUser(null);

			// 🔹 Notify the app
			window.dispatchEvent(new Event('authChanged'));
			
		} catch (err: any) {
			setError(err.message);
			throw err;
		} finally {
			setLoading(false);
		}
	}, []);

	const resetPassword = useCallback(
		async (token: string, password: string) => {
			try {
				setLoading(true);
				setError(null);

				const res = await authService.resetPassword(token, password);
				return res;
			} catch (err: any) {
				setError(err.message);
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	return {
		user,
		loading,
		error,
		login,
		register,
		changePassword,
		forgotPassword,
		resetPassword,
		handleAllSocialLogin,
		logout,
	};
};
